/**
 * Componente RecipientExportButton — Exportação dos destinatários da campanha
 *
 * Reaproveita a arquitetura de `DealColumnExportButton` (Kanban de Negócios):
 * dropdown com CSV/XLS, geração via `xlsx`, BOM UTF-8 e download por Blob URL.
 *
 * A exportação respeita EXATAMENTE os filtros ativos na aba Destinatários
 * (pílula de status + busca), buscando todos os registros do filtro (não apenas
 * a página atual) via a mesma RPC `get_campaign_recipients`.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-06 v1.0.0 - Versão inicial.
 */
import React, { useState } from 'react';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import type { RecipientRow } from './RecipientTable';

// Logger condicional centralizado (padrão do projeto)
const logger = createLogger('RecipientExport');

// Limite alto para trazer todos os destinatários do filtro atual
const EXPORT_LIMIT = 10000;

// Rótulos legíveis por status (espelham a tabela)
const statusLabels: Record<string, string> = {
  pending: 'Pendente',
  processing: 'Processando',
  sent: 'Enviado',
  delivered: 'Entregue',
  read: 'Lido',
  failed: 'Falhou',
  skipped: 'Ignorado',
};

interface RecipientExportButtonProps {
  campaignId: string;
  campaignName: string;
  /** Status atualmente selecionado nas pílulas (null = todos) */
  statusFilter: string | null;
  /** Texto da busca aplicado na listagem */
  search: string;
  /** Total de registros do filtro atual (usado apenas para desabilitar o botão) */
  total?: number;
}

/** Busca todos os destinatários do filtro atual via RPC server-side */
async function fetchAllRecipients(
  campaignId: string,
  tenantId: string,
  statusFilter: string | null,
  search: string,
): Promise<RecipientRow[]> {
  const { data, error } = await supabase.rpc('get_campaign_recipients', {
    p_campaign_id: campaignId,
    p_status_filter: statusFilter,
    p_search: search || null,
    p_limit: EXPORT_LIMIT,
    p_offset: 0,
    p_tenant_hint: tenantId,
  });

  if (error) {
    logger.error('Erro ao buscar destinatários para exportação:', error);
    throw error;
  }

  return (data || []) as unknown as RecipientRow[];
}

/** Formata data/hora ISO para exibição legível pt-BR */
function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('pt-BR');
  } catch {
    return String(value);
  }
}

/**
 * Converte destinatários em linhas planas para planilha.
 * As variáveis da lista importada (jsonb `variables`) viram colunas extras.
 */
function recipientsToRows(recipients: RecipientRow[]) {
  // Descobre todas as chaves de variáveis presentes (união entre linhas)
  const variableKeys = new Set<string>();
  recipients.forEach((r) => {
    const vars = (r as unknown as { variables?: Record<string, unknown> | null }).variables;
    if (vars && typeof vars === 'object') {
      Object.keys(vars).forEach((k) => variableKeys.add(k));
    }
  });

  return recipients.map((r) => {
    const vars = (r as unknown as { variables?: Record<string, unknown> | null }).variables || {};
    const base: Record<string, string | number> = {
      'Nome': r.contact_name || '',
      'Telefone': r.phone || '',
      'Status': statusLabels[r.status] || r.status,
      'Inbox': (r as unknown as { inbox_name?: string | null }).inbox_name || '',
      'Enviado em': formatDateTime(r.sent_at),
      'Entregue em': formatDateTime(r.delivered_at),
      'Lido em': formatDateTime(r.read_at),
      'Erro': r.error_message || '',
    };

    // Colunas dinâmicas das variáveis da lista
    variableKeys.forEach((key) => {
      const value = (vars as Record<string, unknown>)[key];
      base[key] = value == null ? '' : String(value);
    });

    return base;
  });
}

/** Gera e dispara download de arquivo CSV (separador ';' + BOM UTF-8) */
function downloadCSV(recipients: RecipientRow[], fileName: string) {
  const rows = recipientsToRows(recipients);
  if (rows.length === 0) return;

  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(';'),
    ...rows.map((row) =>
      headers
        .map((h) => {
          const val = String(row[h] ?? '');
          // Escapar aspas e envolver quando contiver separador/quebra de linha
          if (val.includes(';') || val.includes('"') || val.includes('\n')) {
            return `"${val.replace(/"/g, '""')}"`;
          }
          return val;
        })
        .join(';'),
    ),
  ].join('\n');

  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, `${fileName}.csv`);
}

/** Gera e dispara download de arquivo XLSX */
function downloadXLS(recipients: RecipientRow[], fileName: string) {
  const rows = recipientsToRows(recipients);
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Destinatarios');

  if (rows.length > 0) {
    const headers = Object.keys(rows[0]);
    worksheet['!cols'] = headers.map((h) => ({ wch: Math.max(h.length, 15) }));
  }

  const xlsBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([xlsBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, `${fileName}.xlsx`);
}

/** Dispara download no navegador via Blob URL */
function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Botão principal de exportação (dropdown CSV/XLS) */
export function RecipientExportButton({
  campaignId,
  campaignName,
  statusFilter,
  search,
  total,
}: RecipientExportButtonProps) {
  const { currentTenant } = useAuth();
  const [exporting, setExporting] = useState(false);

  /** Executa a exportação no formato escolhido, respeitando os filtros ativos */
  const handleExport = async (format: 'csv' | 'xls') => {
    if (!currentTenant?.id || !campaignId) return;

    setExporting(true);
    try {
      const recipients = await fetchAllRecipients(
        campaignId,
        currentTenant.id,
        statusFilter,
        search,
      );

      if (recipients.length === 0) {
        toast.info('Nenhum destinatário encontrado para o filtro atual.');
        return;
      }

      // Nome do arquivo sanitizado: campanha_<nome>_<status>_<data>
      const safeName = (campaignName || 'campanha')
        .replace(/[^a-zA-Z0-9À-ÿ\s-_]/g, '')
        .trim()
        .replace(/\s+/g, '-');
      const statusPart = statusFilter ? statusLabels[statusFilter] || statusFilter : 'todos';
      const dateStr = new Date().toISOString().slice(0, 10);
      const fileName = `campanha_${safeName}_${statusPart}_${dateStr}`.toLowerCase();

      if (format === 'csv') {
        downloadCSV(recipients, fileName);
      } else {
        downloadXLS(recipients, fileName);
      }

      toast.success(`${recipients.length} destinatário(s) exportado(s) com sucesso!`);
      logger.info(`Exportação concluída: ${recipients.length} destinatários`);
    } catch (error) {
      logger.error('Erro na exportação de destinatários:', error);
      toast.error('Erro ao exportar dados. Tente novamente.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="xs"
          className="gap-1.5 h-7"
          disabled={exporting || total === 0}
          title="Exportar destinatários"
        >
          {exporting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          <span>Exportar</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuItem onClick={() => handleExport('csv')} disabled={exporting}>
          <FileText className="mr-2 h-4 w-4" />
          Exportar CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('xls')} disabled={exporting}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Exportar XLS
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
